import React, { useState, useEffect } from 'react';
import { Upload, X, Image as ImageIcon, Film, Loader2 } from 'lucide-react';
import { Progress } from './ui/progress';

/**
 * ImageUploadPreview - CDN-Optimized Version
 * Uses /api/media/upload for DigitalOcean Spaces CDN storage
 * Includes progress tracking and instant local previews
 */
const ImageUploadPreview = ({ onFilesChange, existingFiles = [], folder = null }) => {
  const [uploadedUrls, setUploadedUrls] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadingFiles, setUploadingFiles] = useState([]); // Files being uploaded with local preview

  // Helper to get fresh token (handles refresh if needed)
  const getFreshToken = async () => {
    let token = sessionStorage.getItem('token');
    
    // Check if token might be expired by trying to decode it
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const exp = payload.exp * 1000; // Convert to milliseconds
        const now = Date.now();
        
        // If token expires in less than 30 seconds, try to refresh
        if (exp - now < 30000) {
          const refreshToken = localStorage.getItem('refreshToken');
          if (refreshToken) {
            try {
              const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
              const response = await fetch(`${BACKEND_URL}/api/auth/refresh`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ refresh_token: refreshToken })
              });
              
              if (response.ok) {
                const data = await response.json();
                if (data.access_token) {
                  sessionStorage.setItem('token', data.access_token);
                  token = data.access_token;
                }
              }
            } catch (refreshErr) {
              console.warn('[ImageUpload] Token refresh failed:', refreshErr);
            }
          }
        }
      } catch (e) {
        // Invalid token format, use as-is
      }
    }
    
    return token;
  };

  // Upload a single file with retry on 401
  const uploadFileWithRetry = async (file, folder, onProgress) => {
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    
    const attemptUpload = async (isRetry = false) => {
      const token = isRetry ? await getFreshToken() : sessionStorage.getItem('token');
      
      return new Promise((resolve, reject) => {
        const formData = new FormData();
        formData.append('file', file);
        if (folder) formData.append('folder', folder);
        
        const xhr = new XMLHttpRequest();
        // Use optimize=false for faster uploads - server-side optimization adds latency
        xhr.open('POST', `${BACKEND_URL}/api/media/upload?optimize=false`);
        if (token) {
          xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        }
        
        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable && onProgress) {
            onProgress(Math.round((event.loaded / event.total) * 100));
          }
        };
        
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              resolve(JSON.parse(xhr.responseText));
            } catch (e) {
              reject(new Error('Invalid JSON response'));
            }
          } else if (xhr.status === 401 && !isRetry) {
            // Token expired, retry with fresh token
            attemptUpload(true).then(resolve).catch(reject);
          } else {
            reject(new Error(`Upload failed: ${xhr.status} ${xhr.statusText}`));
          }
        };
        
        xhr.onerror = () => reject(new Error('Network error'));
        xhr.send(formData);
      });
    };
    
    return attemptUpload(false);
  };

  // Initialize from existing files
  useEffect(() => {
    if (existingFiles && existingFiles.length > 0) {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      // Fix URLs that might be relative or from preview domains
      const fixedFiles = existingFiles.map(url => {
        if (!url || typeof url !== 'string') return null;
        
        // If URL contains a preview domain, replace it with current backend URL
        if (url.includes('.preview.emergentagent.com')) {
          const pathMatch = url.match(/(\/api\/.*)/);
          if (pathMatch) {
            return `${BACKEND_URL}${pathMatch[1]}`;
          }
        }
        
        // If already full URL, keep it
        if (url.startsWith('http://') || url.startsWith('https://')) {
          return url;
        }
        
        // If starts with /api/, prepend BACKEND_URL
        if (url.startsWith('/api/')) {
          return `${BACKEND_URL}${url}`;
        }
        
        return url;
      }).filter(url => url !== null);
      
      const currentUrlsStr = JSON.stringify(uploadedUrls);
      const newUrlsStr = JSON.stringify(fixedFiles);
      if (currentUrlsStr !== newUrlsStr) {
        setUploadedUrls(fixedFiles);
      }
    } else if (existingFiles && existingFiles.length === 0 && uploadedUrls.length > 0) {
      setUploadedUrls([]);
    }
  }, [existingFiles]);

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    setUploadProgress(0);
    
    // Create instant local previews for immediate feedback
    const previewFiles = files.map((file, idx) => ({
      id: `temp-${Date.now()}-${idx}`,
      name: file.name,
      preview: URL.createObjectURL(file),
      uploading: true,
      progress: 0
    }));
    setUploadingFiles(previewFiles);
    

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      
      // Upload files one by one with retry support for token refresh
      const newUrls = [];
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        
        try {
          // Use the retry-enabled upload function
          const result = await uploadFileWithRetry(
            file, 
            folder,
            (fileProgress) => {
              // Update individual file progress
              setUploadingFiles(prev => prev.map((f, idx) => 
                idx === i ? { ...f, progress: fileProgress } : f
              ));
              // Update overall progress
              const overallProgress = Math.round(((i + (fileProgress / 100)) / files.length) * 100);
              setUploadProgress(overallProgress);
            }
          );
          
          // Use the CDN URL returned by the media endpoint
          // It returns file_url which could be DO Spaces CDN URL
          const cdnUrl = result.file_url.startsWith('http') 
            ? result.file_url 
            : `${BACKEND_URL}${result.file_url}`;
          newUrls.push(cdnUrl);
          
        } catch (err) {
          console.error(`[ImageUpload] Failed to upload ${file.name}:`, err);
        }
        
        // Update progress after each file
        setUploadProgress(Math.round(((i + 1) / files.length) * 100));
      }
      
      // Update state with new CDN URLs
      const updated = [...uploadedUrls, ...newUrls];
      setUploadedUrls(updated);
      
      
      // Notify parent
      if (onFilesChange) {
        onFilesChange(updated);
      }
      
    } catch (error) {
      console.error('[ImageUpload] Error:', error);
      alert('Failed to upload files. Please try again.');
    } finally {
      setUploading(false);
      setUploadProgress(0);
      setUploadingFiles([]);
      e.target.value = ''; // Reset input
      
      // Cleanup local preview URLs
      previewFiles.forEach(f => URL.revokeObjectURL(f.preview));
    }
  };

  const removeFile = (index) => {
    const updated = uploadedUrls.filter((_, i) => i !== index);
    setUploadedUrls(updated);
    if (onFilesChange) {
      onFilesChange(updated);
    }
  };

  return (
    <div className="space-y-3">
      {/* Upload Button with Progress */}
      <div className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer relative ${
        uploading ? 'border-blue-400 bg-blue-50' : 'border-gray-300 hover:border-blue-400'
      }`}>
        <input
          type="file"
          multiple
          accept="image/*,video/*"
          onChange={handleFileChange}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          disabled={uploading}
        />
        {uploading ? (
          <>
            <Loader2 className="h-10 w-10 mx-auto text-blue-500 mb-2 animate-spin" />
            <p className="text-sm text-blue-600 mb-2 font-medium">
              Uploading to CDN... {uploadProgress}%
            </p>
            <Progress value={uploadProgress} className="h-2 w-3/4 mx-auto" />
          </>
        ) : (
          <>
            <Upload className="h-10 w-10 mx-auto text-gray-400 mb-2" />
            <p className="text-sm text-gray-600 mb-1">Click to upload images/videos</p>
            <p className="text-xs text-gray-500">Max 30MB per file • CDN optimized</p>
          </>
        )}
      </div>

      {/* Upload Preview (files being uploaded with local preview) */}
      {uploadingFiles.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-medium text-blue-600">
            Uploading {uploadingFiles.length} file(s) to CDN...
          </p>
          <div className="grid grid-cols-4 gap-3">
            {uploadingFiles.map((file) => (
              <div
                key={file.id}
                className="relative aspect-square border-2 border-blue-300 rounded-lg overflow-hidden bg-blue-50"
              >
                <img
                  src={file.preview}
                  alt={file.name}
                  className="w-full h-full object-cover opacity-60"
                />
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <Loader2 className="h-6 w-6 text-blue-500 animate-spin mb-1" />
                  <span className="text-xs text-blue-600 font-medium">{file.progress}%</span>
                </div>
                <div className="absolute bottom-0 left-0 right-0 bg-blue-500 h-1">
                  <div 
                    className="bg-blue-600 h-full transition-all duration-200"
                    style={{ width: `${file.progress}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Preview Grid - Show ALL images */}
      {uploadedUrls.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-medium text-gray-700">
            Uploaded Files ({uploadedUrls.length})
          </p>
          <div className="grid grid-cols-4 gap-3 max-h-96 overflow-y-auto">
            {uploadedUrls.map((url, index) => (
              <div
                key={`${url}-${index}`}
                className="relative aspect-square border-2 border-gray-200 rounded-lg overflow-hidden group bg-gray-50"
              >
                <img
                  src={url}
                  alt={`Upload ${index + 1}`}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    console.error('[ImageUpload] Failed to load image:', url);
                    e.target.style.display = 'none';
                    const fallback = e.target.parentElement.querySelector('.fallback-icon');
                    if (fallback) {
                      fallback.classList.remove('hidden');
                      fallback.classList.add('flex');
                    }
                  }}
                  onLoad={() => {
                  }}
                />
                <div className="fallback-icon hidden w-full h-full items-center justify-center bg-gray-100">
                  <div className="text-center">
                    <ImageIcon className="h-12 w-12 text-gray-400 mx-auto mb-1" />
                    <p className="text-xs text-gray-500 px-2">Failed to load</p>
                  </div>
                </div>
                <button
                  onClick={() => removeFile(index)}
                  className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600 shadow-lg"
                  type="button"
                  title={`Remove file ${index + 1}`}
                >
                  <X className="h-4 w-4" />
                </button>
                <div className="absolute bottom-1 left-1 bg-black bg-opacity-60 text-white text-xs px-2 py-1 rounded">
                  #{index + 1}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageUploadPreview;
